import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const statusBadgeVariants = cva(
  'inline-flex h-5 max-w-full items-center gap-1.5 rounded-md border px-1.5 text-[11px] font-medium leading-none',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-muted/40 text-muted-foreground',
        info: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300',
        success:
          'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300',
        warning:
          'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
        danger: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300',
        pending:
          'border-border bg-background text-muted-foreground dark:border-border dark:bg-muted/20',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

const dotTone = {
  neutral: 'bg-muted-foreground/45',
  info: 'bg-sky-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
  pending: 'bg-muted-foreground/35',
};

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  showDot?: boolean;
}

export function StatusBadge({
  tone = 'neutral',
  showDot = false,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  const resolvedTone = tone ?? 'neutral';

  return (
    <span className={cn(statusBadgeVariants({ tone: resolvedTone }), className)} {...props}>
      {showDot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotTone[resolvedTone])} />}
      <span className="truncate">{children}</span>
    </span>
  );
}
